import type { ComponentProps, MouseEvent } from 'react';
import React, { memo } from 'react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BorderProps,
  DisplayProps,
  JustifyContentProps,
  SizeProps,
  SpaceProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  border,
  display,
  justifyContent,
  size,
  space,
  themeGet,
} from 'styled-system';

import type { ColorScheme } from '../theme/colors/types';
import type { BackgroundColorProps, FillProps, OutlineProps } from '../theme/system-types';
import type { CursorProps } from '../theme/system-utilities';
import { cursor, fill, outline } from '../theme/system-utilities';
import { dispatchTrackingEventGA4 } from '../tracking/dispatchTrackingEventGA4';
import { Element } from '../types/tracking';
import type { TrackingParams } from '../types/tracking';
import Icon from './Icon';

const ICON_SIZES = [24, null, null, 32];

interface InfoButtonProps {
  onClick?: (event: MouseEvent) => void;
  className?: string;
  iconProps?: SizeProps & SpaceProps & FillProps & { name?: string };
  ariaLabel?: string;
  eventName?: string;
  trackingParams?: { icon_text?: string } & TrackingParams;
  colorScheme?: ColorScheme;
}

const Button = styled.button<
  DisplayProps &
    AlignItemsProps &
    JustifyContentProps &
    BackgroundColorProps &
    BorderProps &
    SpaceProps &
    CursorProps &
    OutlineProps & {
      colorScheme?: ColorScheme;
    }
>`
  ${display};
  ${alignItems};
  ${justifyContent};
  ${backgroundColor};
  ${border};
  ${space};
  ${cursor};

  &:not(:focus) {
    ${outline};
  }

  &:hover > svg {
    fill: ${({ colorScheme }) => themeGet(`colors.sys.${colorScheme}.icon.hover`)};
  }

  &:active > svg {
    fill: ${({ colorScheme }) => themeGet(`colors.sys.${colorScheme}.icon.active`)};
  }

  &:focus-visible {
    border-radius: 100%;
    outline: 2px solid ${themeGet('colors.sys.secondary.border.focus')};
  }
`;

const buttonStyles: ComponentProps<typeof Button> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
  border: 'none',
  p: 'sp_0',
  type: 'button',
  cursor: 'pointer',
  outline: 'none',
};

const InfoIcon = styled(Icon)<any>`
  ${size};
  ${space};
  ${fill};
`;

const getIconFill = (colorScheme: ColorScheme) => {
  switch (colorScheme) {
    case 'secondary':
      return 'sys.secondary.icon.strong';
    default:
      return `sys.${colorScheme}.icon.default`;
  }
};

const InfoButton = ({
  onClick,
  className,
  iconProps,
  eventName,
  ariaLabel,
  colorScheme = 'neutral',
  trackingParams,
}: InfoButtonProps) => {
  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    onClick?.(event);

    if (eventName && trackingParams?.icon_text) {
      dispatchTrackingEventGA4({
        event: eventName,
        element: Element.Icon,
        ...trackingParams,
      });
    }
  };

  return (
    <Button
      className={className}
      onClick={handleClick}
      data-testid="info-button"
      aria-label={ariaLabel ?? 'Info'}
      colorScheme={colorScheme}
      {...buttonStyles}
    >
      <InfoIcon
        {...iconProps}
        name="info"
        size={iconProps?.size ?? ICON_SIZES}
        fill={iconProps?.fill ?? getIconFill(colorScheme)}
      />
    </Button>
  );
};

export default memo(InfoButton);
